// components/pages/MainDepartmentPage.js - Updated with DeskCarousel for desktop feeds with proper navigation
// Theme-aware version - uses CSS variables from theme.css
import './MainDepartmentPage.css';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Image } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { People } from 'react-bootstrap-icons';
import FeedSection from '../feed/FeedSection';
import DeskCarousel from '../carousel/DeskCarousel';
import MobileFeedCarousel from '../feed/MobileFeedCarousel';
import NotificationButton from '../notifications/NotificationButton';
import SearchNavBar from '../search/SearchNavBar';
import MobileNavigation from '../navigation/MobileNavigation';
import { getCountryConfig, getCountryDepartmentConfig } from '../../utils/multiCountryConfig.js';
import openCapacitorSite from '../../utils/browserUtils';
import { MembersCarousel, MEMBER_TYPES } from '../members';
import MembersCarouselSection from '../members/MembersCarouselSection';

// Reusable icon/image component with fallback
// Uses currentColor to inherit from parent's CSS variable
const DepartmentIcon = ({ image, name, size = 70, mobileSize = 60 }) => {
  const [imgError, setImgError] = useState(false);
  
  if (!image || imgError) {
    return <People size={size} className="department-icon-fallback" />;
  }
  
  return (
    <Image 
      src={image} 
      alt={name}
      onError={() => setImgError(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

const MainDepartmentPage = ({ 
  country = 'us', 
  department,
  maxItemsPerFeed = 1,
  carouselInterval = 5000,
  useMobileLayout = null
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  // Check if this is the White House page
  const isWhiteHouse = department === 'whitehouse' && country === 'us';
  
  // Auto-detect mobile layout (MOVED BEFORE CONFIG CALLS)
  useEffect(() => {
    if (useMobileLayout !== null) {
      setIsMobile(useMobileLayout);
      return;
    }

    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [useMobileLayout]);
  
  // Get the department configuration for this country (MOVED AFTER HOOKS)
  const departmentConfig = getCountryDepartmentConfig(country, department);
  const countryConfig = getCountryConfig(country);

  // Map department to proper branch for notification topics - EXPANDED for all departments
  const getDepartmentBranch = (departmentKey) => {
    switch (departmentKey?.toLowerCase()) {
      case 'defense':
      case 'defence':
        return 'defense';
      case 'treasury':
        return 'economy';
      case 'state':
        return 'foreign affairs';
      case 'health':
      case 'healthandhuman':
        return 'health';
      case 'justice':
        return 'justice';
      case 'interior':
      case 'agriculture':
      case 'energy':
        return 'environment';
      case 'transportation':
      case 'housing':
        return 'infrastructure';
      case 'education':
      case 'labor':
      case 'commerce':
        return 'economy';
      case 'veterans':
        return 'defense';
      case 'homeland':
        return 'defense';
      default:
        return 'executive'; // Default to executive branch
    }
  };
  
  // Handle department-level notification state changes
  const handleDepartmentNotificationChange = (isSubscribed, feedId, feedName, branch, topic) => {
    console.log(`Department notification ${isSubscribed ? 'enabled' : 'disabled'}:`, {
      country,
      department,
      feedId, 
      feedName, 
      branch, 
      topic
    });
  };

  /**
   * Generate feed variations with different skip values for carousel display
   * This creates multiple "slides" from the same feed source (matching SectionPage pattern)
   */
  const generateFeedVariations = (baseFeed, count = 10) => {
    const variations = [];
    for (let i = 0; i < count; i++) {
      let endpoint = baseFeed.apiEndpoint || baseFeed.apiParams;
      
      // Replace existing skip value or append skip parameter
      if (endpoint.includes('skip=')) {
        endpoint = endpoint.replace(/skip=\d+/, `skip=${i}`);
      } else {
        endpoint += `&skip=${i}`;
      }
      
      variations.push({
        ...baseFeed,
        id: `${baseFeed.id}-${i}`,
        apiEndpoint: endpoint,
        title: baseFeed.title || baseFeed.name
      });
    }
    return variations;
  };

  /**
   * Transform department feed to match DeskCarousel expected format
   * IMPORTANT: Must include type: 'normal' for flattenSectionsToFeeds to recognize it
   */
  const transformFeedForCarousel = (feed) => ({
    type: 'normal',  // Required for DeskCarousel's flattenSectionsToFeeds
    id: feed.id,
    title: feed.name,
    apiEndpoint: feed.apiParams,
    feedName: feed.name,
    branch: feed.feedBranch || 'Executive',
    topic: feed.feedTopic || 'N/A',
    buttonName: feed.buttonName || 'Document',
    viewAllLink: feed.viewAllLink
  });

  // Early return AFTER all hooks have been called
  if (!departmentConfig) {
    return (
      <div className="main-department-page-root error-state" style={{ padding: '20px' }}>
        <h1>Department not found: {department} for {country}</h1>
        <p>Available departments:</p>
        <ul>
          {Object.keys(countryConfig?.departmentConfig || {}).map(key => (
            <li key={key}>{key}</li>
          ))}
        </ul>
      </div>
    );
  }

  const { departmentInfo, mainFeeds, agencies = [] } = departmentConfig;

  // Determine proper branch for this department
  const departmentBranch = getDepartmentBranch(department);
  const departmentNotificationId = `dept-${country}-${department}`;
  const departmentNotificationName = `${departmentInfo.name} Updates`;

  // Create mobile feeds from department configuration
  const createMobileFeeds = () => {
    const feeds = [];
    
    // Add main department feeds
    mainFeeds.forEach(feed => {
      feeds.push({
        id: feed.id,
        name: feed.name,
        displayType: 'normal',
        apiParams: feed.apiParams,
        feedBranch: feed.feedBranch || 'Executive',
        feedTopic: feed.feedTopic || 'N/A',
        buttonName: feed.buttonName || 'Document',
        departmentPage: false // Main feeds don't navigate to sub-pages
      });
    });
    
    // Add agency feeds with navigation capability (for US departments with sub-agencies)
    agencies.forEach(agency => {
      feeds.push({
        id: agency.id,
        name: agency.name,
        displayType: 'normal',
        apiParams: agency.apiParams,
        feedBranch: 'Executive',
        feedTopic: agency.topic,
        buttonName: agency.buttonName || 'Document',
        departmentPage: true, // Agency feeds can navigate to sub-pages
        agencyRoute: agency.route || `/${country}/${department}/${agency.id}` // Route for the agency page
      });
    });
    
    return feeds;
  };

  // Mobile Layout
  if (isMobile) {
    const mobileFeeds = createMobileFeeds();
    
    return (
      <div id="homebg" className="main-department-page-root">
            <br />
        {/* Match HomePage spacing - 70px navbar clearance */}
        <div style={{ marginTop: '45px', paddingTop: '5px' }}>
          <div className="mobile-section-header">
            <div className="mobile-profile-container">
              <div className="mobile-profile-image">
                <DepartmentIcon 
                  image={departmentInfo.image} 
                  name={departmentInfo.name} 
                  size={45}
                />
              </div>
              
              <div className="mobile-profile-content">
                <div className="mobile-profile-title">
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    width: '100%',
                    gap: '8px'
                  }}>
                    <h3 style={{ 
                      margin: 0, 
                      flex: 1, 
                      textAlign: 'center',
                      paddingRight: '50px',
                      fontSize: '17px'
                    }}>{departmentInfo.name}</h3>
                    
                    <div style={{ flexShrink: 0 }}>
                      {/* <NotificationButton
                        feedId={departmentNotificationId}
                        feedName={departmentNotificationName}
                        branch={departmentBranch}
                        topic="Department"
                        variant="outline-light"
                        size="sm"
                        showLabel={false}
                        onNotificationStateChange={handleDepartmentNotificationChange}
                      /> */}
                    </div>
                  </div>
                </div>
                
                <div className="mobile-profile-description">
                  <span>{departmentInfo.missionStatement}</span>
                  
                  <div className="mobile-learn-more">
                    <Link 
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openCapacitorSite(departmentInfo.officialURL, "_blank");
                      }}
                    >
                      Visit Official Website
                    </Link>
                  </div>
                  
                  {/* {departmentInfo.establishingActURL && (
                    <div className="mobile-external-links">
                      <Button id='standardButton'
                        className="mobile-external-link-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          openCapacitorSite(departmentInfo.establishingActURL, "_blank");
                        }}
                      >
                        Establishing Act
                      </Button>
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          </div>
          
          {/* Presidents Carousel for White House - Mobile */}
          {isWhiteHouse && (
            <div className="department-carousel-wrapper department-members-carousel">
              <MembersCarousel
                memberType={MEMBER_TYPES.PRESIDENT}
                limit={10}
                title="U.S. Presidents"
                showHeading={true}
                showViewAll={true}
              />
            </div>
          )}

          {/* Vice Presidents Carousel for White House - Mobile */}
          {isWhiteHouse && (
            <div className="department-carousel-wrapper department-members-carousel">
              <MembersCarousel
                memberType={MEMBER_TYPES.VICE_PRESIDENT}
                limit={10}
                title="U.S. Vice Presidents"
                showHeading={true}
                showViewAll={true}
              />
            </div>
          )}
          
          <div className="department-carousel-wrapper mobile-feeds-container">
            <MobileFeedCarousel feeds={mobileFeeds} />
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout - with DeskCarousel for feeds (matching SectionPage pattern)
  // Group main feeds into pairs for rows
  const feedRows = [];
  for (let i = 0; i < mainFeeds.length; i += 2) {
    feedRows.push(mainFeeds.slice(i, i + 2));
  }

  return (
    <div id='homebg' className="main-department-page-root">
      {/* Desktop Department Header */}
      <br />
      <div style={{ position: 'relative', padding: '30px 0' }}>
        <div id='profileImage' style={{ 
          width: '130px', 
          height: '130px', 
          borderRadius: '100%', 
          overflow: 'hidden', 
          marginLeft: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <DepartmentIcon 
            image={departmentInfo.image} 
            name={departmentInfo.name} 
            size={70}
          />
        </div>

        <div style={{ 
          position: 'absolute', 
          top: '0', 
          left: '250px', 
          display: 'flex', 
          flexDirection: 'column',
          width: 'calc(100% - 250px)'
        }}>
          <div style={{ fontSize: '20px' }}> 
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0 }}>{departmentInfo.name}</h3>
              
              {/* <NotificationButton
                feedId={departmentNotificationId}
                feedName={departmentNotificationName}
                branch={departmentBranch}
                topic="Department"
                variant="outline-light"
                size="sm"
                showLabel={true}
                onNotificationStateChange={handleDepartmentNotificationChange}
              /> */}
            </div>
            
            <div className="mission-text" style={{ fontSize: '16px', width: '80%' }}>
              <span>{departmentInfo.missionStatement}</span>
              <br />
              <div style={{ fontSize: '18px', marginTop: '10px' }}>
                <span>
                  <Link 
                    onClick={(e) => {
                      e.preventDefault();
                      openCapacitorSite(departmentInfo.officialURL, "_blank");
                    }}
                  >
                    Visit Official Website
                  </Link>
                </span>
              </div>
              
              {/* {departmentInfo.establishingActURL && (
                <div style={{ marginTop: '15px' }}>
                  <Button 
                    size='md' 
                    id='navbarAboutButtonDesktop' 
                    variant='primary' 
                    className="me-2"
                    onClick={(e) => {
                      e.preventDefault();
                      openCapacitorSite(departmentInfo.establishingActURL, "_blank");
                    }}
                  >
                    View Establishing Act
                  </Button>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>
      
      <br /><br />

      {/* Presidents & Vice Presidents Carousels for White House - Desktop (same layout as feeds) */}
      {isWhiteHouse && (
        <div id='branchPage' style={{ marginBottom: '1rem' }}>
          <Container className='bgcolor'>
            <Container id='carouselBufferOne'>
              <Row>
                <Col md={6}>
                  <MembersCarouselSection
                    memberType={MEMBER_TYPES.PRESIDENT}
                    limit={10}
                    title="U.S. Presidents"
                    showViewAll={true}
                  />
                </Col>
                <Col md={6}>
                  <MembersCarouselSection
                    memberType={MEMBER_TYPES.VICE_PRESIDENT}
                    limit={10}
                    title="U.S. Vice Presidents"
                    showViewAll={true}
                  />
                </Col>
              </Row>
            </Container>
          </Container>
        </div>
      )}

      {/* Main Department Feeds - Using DeskCarousel (matching SectionPage pattern) */}
      {/* showFeedTitle={false} lets DeskCarousel generate feed URL for FullFeedView navigation */}
      {feedRows.map((row, rowIndex) => (
        <div key={`feed-row-${rowIndex}`} id='branchPage' style={{ marginBottom: '1rem' }}>
          <Container className='bgcolor'>
            <Container id='carouselBufferOne'>
              <Row>
                {row.map((feed) => {
                  const transformedFeed = transformFeedForCarousel(feed);
                  
                  return (
                    <Col key={feed.id} md={6}>
                      <DeskCarousel 
                        title={feed.name}
                        sections={generateFeedVariations(transformedFeed, 10)}
                        viewAllLink={feed.viewAllLink || `/${country}/${department}`}
                        intervalDuration={carouselInterval}
                        showAll={true}
                        showFeedTitle={false}
                        useViewAllLinkForTitle={false}  // Use default behavior: generate feed URL
                      />
                    </Col>
                  );
                })}
              </Row>
            </Container>
          </Container>
        </div>
      ))}

      {/* Agencies - Using DeskCarousel with navigation to AgencyPage */}
      {/* useViewAllLinkForTitle={true} ensures clicking navigates to AgencyPage, not feed */}
      {agencies.length > 0 && (
        <>
          {/* Group agencies into rows of 2 */}
          {Array.from({ length: Math.ceil(agencies.length / 2) }, (_, i) => {
            const agencyPair = agencies.slice(i * 2, i * 2 + 2);
            
            return (
              <div key={`agency-row-${i}`} id='branchPage' style={{ marginBottom: '1rem' }}>
                <Container className='bgcolor'>
                  <Container id='carouselBufferTwo'>
                    <Row>
                      {agencyPair.map((agency) => {
                        // Generate agency route - navigates to AgencyPage
                        const agencyRoute = agency.route || `/${country}/${department}/${agency.id}`;
                        
                        const transformedAgency = {
                          type: 'normal',  // Required for DeskCarousel's flattenSectionsToFeeds
                          id: agency.id,
                          title: agency.name,
                          apiEndpoint: agency.apiParams,
                          feedName: agency.feedName || agency.name,
                          branch: 'Executive',
                          topic: agency.topic,
                          buttonName: agency.buttonName,
                          viewAllLink: agencyRoute
                        };
                        
                        return (
                          <Col key={agency.id} md={6}>
                            <DeskCarousel 
                              title={agency.name}
                              sections={generateFeedVariations(transformedAgency, 10)}
                              viewAllLink={agencyRoute}
                              intervalDuration={carouselInterval}
                              showAll={true}
                              showFeedTitle={false}
                              useViewAllLinkForTitle={true}  // Force use of viewAllLink for AgencyPage navigation
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  </Container>
                </Container>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default MainDepartmentPage;